import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewEncapsulation, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { SelectionModel } from '@angular/cdk/collections';

import { SabisTranslateService } from '../translation/sdp-translate.service';

import { SabisTreeFlatNode, SabisTreeNode } from './sabis-tree-combobox.models';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'sabis-tree-combobox',
  templateUrl: './sabis-tree-combobox.component.html',
  styleUrls: ['./sabis-tree-combobox.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SabisTreeComboboxComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SabisTreeComboboxComponent),
      multi: true,
    }
  ]
})
export class SabisTreeComboboxComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input('scLabel') scLabel: string = '';
  @Input('scData') scData: SabisTreeNode[] = [];
  @Input('scMultiple') scMultiple: boolean = false;
  @Input('scHideValidationMessage') scHideValidationMessage: boolean = false;

  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) autoCompleteInput: MatAutocompleteTrigger | undefined;

  public filterText: string = '';

  public isDisabled: boolean = false;
  public isValid: boolean = true;
  public validationMessage: string = '';

  public treeItems: SabisTreeNode[] = [];
  public selectedValues: number[] = [];

  /** The selection for checklist */
  private _checklistSelection: SelectionModel<SabisTreeFlatNode> = new SelectionModel<SabisTreeFlatNode>(true);
  public selectionSummary: string = '';

  private onChangeCallback: null | ((value: any[]) => void) = null;
  private onTouchedCallback: null | (() => void) = null;

  constructor(private _translateService: SabisTranslateService) { }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges) {
    const scDataChange = changes['scData'];
    if (scDataChange && this.scData) {

      this.treeItems = this.scData;
      this.dataSource.data = this.scData;
    }
  }

  writeValue(values: any[]): void {
    if (this.selectedValues === values) {
      return;
    }

    this.selectedValues = [];

    if (values && values.length > 0) {
      this.selectedValues = values;

      this.selectedValues.forEach(element => {
        let nodeToSelect = this.getLeafNodeById(element)!;
        this.checkboxClick(nodeToSelect, true, true);
      });
    }
    else {
      // uncheck all nodes
      this.deselectTreeNodes(this.treeControl.dataNodes);
      this._checklistSelection.clear();
    }

    // update selection info text
    this.updateSelectionText();
  }

  registerOnChange(onChange: any): void {
    this.onChangeCallback = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouchedCallback = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Open auto complete panel (Needed because the input is readonly)
   * @param event
   */
  public openPanel(event: any): void {
    event.stopPropagation();
    if (this.autoCompleteInput) {
      this.autoCompleteInput.openPanel();
    }
  }

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<SabisTreeFlatNode, SabisTreeNode>();
  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<SabisTreeNode, SabisTreeFlatNode>();

  getLevel = (node: SabisTreeFlatNode) => node.level;
  isExpandable = (node: SabisTreeFlatNode) => node.isExpandable;
  getChildren = (node: SabisTreeNode): SabisTreeNode[] | undefined => node.children;

  hasChild = (_: number, _nodeData: SabisTreeFlatNode) => _nodeData.isExpandable;

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: SabisTreeNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    let flatNode: SabisTreeFlatNode | null =
      existingNode && existingNode.itemId === node.itemId
        ? existingNode
        : null;
    // create new mapped entity
    if (flatNode === null) {
      flatNode = {
        itemId: node.itemId,
        displayName: node.displayName,
        isExpandable: !!node.children && node.children.length > 0,
        level: level,
        isChecked: false,
        isPartiallyChecked: false
      };
      this.flatNodeMap.set(flatNode, node);
      this.nestedNodeMap.set(node, flatNode);
    }
    return flatNode;
  };

  treeFlattener: MatTreeFlattener<SabisTreeNode, SabisTreeFlatNode> = new MatTreeFlattener(
    this.transformer,
    this.getLevel,
    this.isExpandable,
    this.getChildren
  );

  treeControl: FlatTreeControl<SabisTreeFlatNode> = new FlatTreeControl<SabisTreeFlatNode>(
    this.getLevel,
    this.isExpandable
  );

  dataSource: MatTreeFlatDataSource<SabisTreeNode, SabisTreeFlatNode> = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  /** Toggle the tree checkboxes. Select/deselect all the descendants node (UI) */
  public checkboxClick(node: SabisTreeFlatNode, isLeaf: boolean, skipEvent: boolean): void {
    node.isPartiallyChecked = false;
    node.isChecked = !node.isChecked;

    // no need to update other nodes if single selection
    if (this.scMultiple) {
      this._checklistSelection.toggle(node);

      // update descendants state
      this.updateDescendantsStateRecursive(node, node.isChecked);

      // update parents state
      this.updateParentsStateRecursive(node);
    }
    else {
      // keep only one node checked if tree is single select
      this._checklistSelection.selected.forEach(n => n.isChecked = false);
      this._checklistSelection.clear();
      node.isChecked ? this._checklistSelection.select(node) : this._checklistSelection.deselect(node);
    }

    // update selection info text
    this.updateSelectionText();

    // update selected items ids + emit event
    this.updateSelectedValues();

    if (this.onChangeCallback && !skipEvent) {
      this.onChangeCallback(this.selectedValues);
    }
  }

  private updateDescendantsStateRecursive(node: SabisTreeFlatNode, isChecked: boolean): void {
    const descendants: SabisTreeFlatNode[] = this.treeControl.getDescendants(node);

    descendants.forEach(d => {
      d.isPartiallyChecked = false;
      d.isChecked = isChecked;
      isChecked ? this._checklistSelection.select(d) : this._checklistSelection.deselect(d);

      this.updateDescendantsStateRecursive(d, isChecked);
    });
  }

  private updateParentsStateRecursive(node: SabisTreeFlatNode): void {
    let parent: SabisTreeFlatNode | null = this.getParentNode(node);

    while (parent) {
      const descendants: SabisTreeFlatNode[] = this.treeControl.getDescendants(parent);
      var checkedCount = descendants.filter(d => d.isChecked).length;
      parent.isChecked = descendants.length === checkedCount;
      parent.isPartiallyChecked = (checkedCount > 0 && descendants.length !== checkedCount)
        ||
        descendants.some(d => d.isPartiallyChecked);

      // update next parent
      parent = this.getParentNode(parent);
    }
  }

  /* Get the parent node of a node */
  private getParentNode(node: SabisTreeFlatNode): SabisTreeFlatNode | null {
    const currentLevel = this.getLevel(node)!;

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode)! < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private getLeafNodeById(nodeId: number): SabisTreeFlatNode | null {
    let result = this.treeControl.dataNodes.find(node => node.isExpandable == false && node.itemId === nodeId) ?? null;

    return result;
  }

  protected onFilterChanged(event: any): void {
    let searchText = event;
    let filteredTreeData;

    if (searchText) {
      // Filter the tree
      function filter(array: any[] | undefined, text: string) {
        const getChildren = (result: any[], object: { displayName: string; children: any[]; }) => {
          if (object.displayName.toLowerCase().includes(text.toLowerCase())) {
            result.push(object);
            return result;
          }
          if (Array.isArray(object.children)) {
            const children = object.children.reduce(getChildren, []);
            if (children.length) result.push({ ...object, children });
          }
          return result;
        };

        return array!.reduce(getChildren, []);
      }

      filteredTreeData = filter(this.treeItems, searchText);
    } else {
      // Return the initial tree
      filteredTreeData = this.treeItems;
    }

    // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
    this.dataSource.data = filteredTreeData;

    // update state of parents
    this.treeControl.dataNodes.forEach(n => {
      this.updateParentsStateRecursive(n);
    });
  }

  public clearSearch(event: any): void {
    this.filterText = '';
    this.searchInput.nativeElement.focus();
    this.onFilterChanged('');
    event.stopImmediatePropagation();
  }

  protected updateSelectionText(): void {
    this.selectionSummary = this._checklistSelection.selected.filter(item => item.isExpandable === false).map(item => item.displayName).join(",");
    this.selectionSummary = this.selectionSummary.replace(/<(?:.|\s)*?>/g, '');
  }

  /**
   * Deselect tree nodes and their descendants
   * @param nodes
   */
  public deselectTreeNodes(nodes: SabisTreeFlatNode[]): void {
    nodes.forEach(node => {
      node.isChecked = false;
      this._checklistSelection.deselect(node);

      const descendants: SabisTreeFlatNode[] = this.treeControl.getDescendants(node);
      descendants.forEach(d => {
        d.isChecked = false;
        this._checklistSelection.deselect(d);

        this.deselectTreeNodes([d]);
      });
    })
  }

  /**
   * Select tree nodes and their descendants
   * @param nodes
   */
  public selectTreeNodes(nodes: SabisTreeFlatNode[]): void {
    nodes.forEach(node => {
      node.isChecked = true;
      this._checklistSelection.select(node);

      const descendants: SabisTreeFlatNode[] = this.treeControl.getDescendants(node);
      descendants.forEach(d => {
        d.isChecked = true;
        this._checklistSelection.select(d);

        this.selectTreeNodes([d]);
      });
    });
  }

  /**
   * Select all tree nodes
   */
  public selectAllTreeNodes(): void {
    this.selectTreeNodes(this.treeControl.dataNodes);

    // update selected values
    this.updateSelectedValues();
    // update selection info text
    this.updateSelectionText();
  }

  validate(c: AbstractControl): { [key: string]: any; } {
    this.setValidationMessage(c);
    return {};
  }

  private updateSelectedValues(): void {
    this.selectedValues = this._checklistSelection.selected.filter(n => n.isExpandable === false).map(n => n.itemId);
  }

  private setValidationMessage(c: AbstractControl): void {
    // set Time out because the control changes validation after passing in the validate function
    setTimeout(() => {
      this.isValid = c.errors == null;
      if (!this.isValid) {
        for (var e in c.errors) {
          if (e == 'required') {
            this.validationMessage = this._translateService.instant('SabisComponents.FieldRequired');
          }
          else {
            this.validationMessage = '';
          }
        }
      }
    });
  }
}
